import type { CodingProgramBlock } from './coding'
import type { MotorControl, MotorDirection } from './motor-control'

export type CodingRuntimeResult = 'completed' | 'cancelled'

type CodingRuntimeHandlers = {
  setMotor: (cubeInstanceId: string, control: MotorControl) => void
  stopAll: (cubeInstanceIds: string[]) => void
  onBlock?: (block: CodingProgramBlock) => void
  readDistanceCm?: (sensorNumber: number) => number
}

type CodingRuntimeOptions = {
  signal: AbortSignal
  sleep?: (durationMs: number, signal: AbortSignal) => Promise<boolean>
  maxForeverCycles?: number
}

function finiteNumber(value: string | number | undefined, fallback: number) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function direction(value: string | number | undefined): MotorDirection {
  return value === 'reverse' ? 'reverse' : 'forward'
}

export function codingMotorCubeNumbers(blocks: CodingProgramBlock[]) {
  const numbers = new Set<number>()
  const visit = (items: CodingProgramBlock[]) => items.forEach((block) => {
    if (['motor-for', 'motor-start', 'motor-stop'].includes(block.type)) {
      numbers.add(Math.max(1, Math.floor(finiteNumber(block.values.cube, 1))))
    }
    visit(block.children)
  })
  visit(blocks)
  return [...numbers].sort((left, right) => left - right)
}

export function abortableCodingDelay(durationMs: number, signal: AbortSignal) {
  if (signal.aborted) return Promise.resolve(false)
  return new Promise<boolean>((resolve) => {
    const finish = (completed: boolean) => {
      signal.removeEventListener('abort', onAbort)
      clearTimeout(timer)
      resolve(completed)
    }
    const onAbort = () => finish(false)
    const timer = setTimeout(() => finish(true), Math.max(0, durationMs))
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

export async function runCodingProgram(
  blocks: CodingProgramBlock[],
  cubeInstanceIds: string[],
  handlers: CodingRuntimeHandlers,
  options: CodingRuntimeOptions,
): Promise<CodingRuntimeResult> {
  const sleep = options.sleep ?? abortableCodingDelay
  const maximumForeverCycles = options.maxForeverCycles ?? Number.POSITIVE_INFINITY

  const pause = (durationMs: number) => sleep(durationMs, options.signal)
  const cubeFor = (block: CodingProgramBlock) => {
    const cubeNumber = Math.max(1, Math.floor(finiteNumber(block.values.cube, 1)))
    return cubeInstanceIds[cubeNumber - 1] ?? null
  }
  const stopCube = (cubeInstanceId: string) => handlers.setMotor(cubeInstanceId, {
    direction: 'stop',
    speedPercent: 0,
  })

  const executeBlocks = async (items: CodingProgramBlock[]): Promise<boolean> => {
    for (const block of items) {
      if (options.signal.aborted) return false
      handlers.onBlock?.(block)

      if (block.type === 'motor-for') {
        const cubeInstanceId = cubeFor(block)
        if (!cubeInstanceId) continue
        handlers.setMotor(cubeInstanceId, {
          direction: direction(block.values.direction),
          speedPercent: Math.min(100, Math.max(0, finiteNumber(block.values.speed, 20))),
        })
        const completed = await pause(Math.max(0.05, finiteNumber(block.values.seconds, 1)) * 1000)
        stopCube(cubeInstanceId)
        if (!completed) return false
        continue
      }

      if (block.type === 'motor-start') {
        const cubeInstanceId = cubeFor(block)
        if (cubeInstanceId) handlers.setMotor(cubeInstanceId, {
          direction: direction(block.values.direction),
          speedPercent: Math.min(100, Math.max(0, finiteNumber(block.values.speed, 20))),
        })
        if (!await pause(80)) return false
        continue
      }

      if (block.type === 'motor-stop') {
        const cubeInstanceId = cubeFor(block)
        if (cubeInstanceId) stopCube(cubeInstanceId)
        if (!await pause(80)) return false
        continue
      }

      if (block.type === 'stop-all') {
        handlers.stopAll(cubeInstanceIds)
        if (!await pause(80)) return false
        continue
      }

      if (block.type === 'wait') {
        if (!await pause(Math.max(0.05, finiteNumber(block.values.seconds, 1)) * 1000)) return false
        continue
      }

      if (block.type === 'repeat') {
        const count = Math.min(100, Math.max(1, Math.floor(finiteNumber(block.values.count, 1))))
        for (let cycle = 0; cycle < count; cycle += 1) {
          if (!await executeBlocks(block.children)) return false
        }
        continue
      }

      if (block.type === 'forever') {
        let cycle = 0
        while (!options.signal.aborted && cycle < maximumForeverCycles) {
          if (block.children.length) {
            if (!await executeBlocks(block.children)) return false
          } else if (!await pause(100)) return false
          cycle += 1
        }
        if (options.signal.aborted) return false
        continue
      }

      if (block.type === 'if-distance') {
        const sensorNumber = Math.max(1, Math.floor(finiteNumber(block.values.sensor, 1)))
        const measuredDistance = handlers.readDistanceCm?.(sensorNumber) ?? Number.POSITIVE_INFINITY
        if (measuredDistance < finiteNumber(block.values.distance, 10)) {
          if (!await executeBlocks(block.children)) return false
        }
        continue
      }

      if (block.type === 'read-distance' || block.type === 'button-state'
        || block.type === 'set-variable' || block.type === 'change-variable') {
        if (!await pause(80)) return false
      }
    }
    return !options.signal.aborted
  }

  const completed = await executeBlocks(blocks)
  if (!completed) handlers.stopAll(cubeInstanceIds)
  return completed ? 'completed' : 'cancelled'
}
